import React, {CSSProperties, ReactNode} from "react";
import AbstractAnnotation, {AbstractAnnotationProperties} from "./AbstractAnnotation";
import DomHelper from "../../../../shared/helper/DomHelper";
import AnnotationRectangle from "./AnnotationRectangle";
import LengthUnit from "../../../../shared/units/LengthUnit";
import {
	Annotation,
	AnnotationPointPoint,
	AnnotationPointPointInterface, BorderStyle,
	Markup,
	MetadataRectangle
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {SVG_NAMESPACE} from "../../../../shared/annotation/Annotation";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export class LineAnnotation extends AbstractAnnotation<AbstractAnnotationProperties> {
	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let lineMarkerId: string = DomHelper.generateUUID();
		let annotation: Annotation = Annotation.fromJson(this.props.annotation.get());
		let color: string = FunctionHelper.applyValue(annotation.color, "transparent");
		let points: Array<AnnotationPointPoint> = this.getPoints();
		let lineBoundaries: MetadataRectangle = this.getLineBoundaries();

		if (points.length > 0) {
			lineBoundaries.width! -= lineBoundaries.x!;
			lineBoundaries.height! -= lineBoundaries.y!;

			let result: Array<AnnotationPointPoint> = [];
			for (let point of points) {
				result.push(AnnotationPointPoint.fromJson({
					x: point.x! - lineBoundaries.x!,
					y: lineBoundaries.height! - (point.y! - lineBoundaries.y!)
				} as AnnotationPointPointInterface));
			}

			points = result;
		}

		let lineFillColor: string = FunctionHelper.exists(annotation.border) ? annotation.border!.interiorColor! : color;
		let annotationStyle: CSSProperties = {
			opacity: FunctionHelper.applyValue(annotation.markup?.opacity, Markup.getOpacityDefault()) / 100,
			stroke: color
		};

		if (FunctionHelper.exists(annotation.border)) {
			annotationStyle.strokeWidth = annotation.border!.width!.toString();

			if (annotation.border!.style === BorderStyle.Dashed) {
				annotationStyle.strokeDasharray = JSON.parse(annotation.border!.dashPattern!).join(",");
			}
		}

		let popupContent: string | undefined;
		let popupCreator: string | undefined;
		let popupColor: string | undefined;

		if (!annotation.line?.showCaption) {
			popupContent = annotation.contents;
			popupCreator = annotation.markup?.creator;
			popupColor = color;
		}

		let captionElement: ReactNode = null;
		if (annotation.line?.showCaption) {
			let mp1: AnnotationPointPoint = AnnotationPointPoint.fromJson(
				{x: points[1].x! - points[0].x!, y: points[1].y! - points[0].y!}
			);
			let mp2: AnnotationPointPoint = AnnotationPointPoint.fromJson({x: 1, y: 0});

			let direction: number = points[1].y! - points[0].y! < 0 ? -1 : 1;
			let rotation: number =
				Math.acos((mp1.x! * mp2.x! + mp1.y! * mp2.y!) /
					(
						(Math.sqrt(mp1.x! * mp1.x! + mp1.y! * mp1.y!)) *
						(Math.sqrt(mp2.x! * mp2.x! + mp2.y! * mp2.y!))
					)
				);
			rotation = (360 / (2 * Math.PI)) * rotation * direction;

			captionElement = (
				<text x="50%" y="50%" style={{
					textAnchor: "middle",
					fontSize: "8px",
					fontFamily: "Helvetica, sans-serif",
					transformOrigin: "50% 50%",
					transform: "translate(" +
						annotation.line.captionHorizontalOffset + LengthUnit.PIXEL + "," +
						(-1 * annotation.line.captionVerticalOffset!) + LengthUnit.PIXEL +
						") " +
						"rotate(" + rotation + "deg)"
				}}>
					{annotation.contents}
				</text>
			);
		}

		return (
			<AnnotationRectangle {...this.props} {...lineBoundaries}
								 className={"annotation annotation-" + annotation.subType!.toLowerCase()}
								 popupCreator={popupCreator} popupColor={popupColor} popupContent={popupContent}
								 creationDate={annotation.markup?.creationDate}
								 style={{
									 zIndex: 0
								 }}>
				<svg xmlns={SVG_NAMESPACE}>
					{this.renderMarker(lineMarkerId, color, lineFillColor)}
					<polyline points={points.map(point => point.x + "," + point.y).join(" ")}
							  markerStart={"url(#lineStart-" + lineMarkerId + ")"}
							  markerEnd={"url(#lineEnd-" + lineMarkerId + ")"}
							  style={annotationStyle}/>
					{captionElement}
				</svg>
			</AnnotationRectangle>
		);
	}
}